/**
 * Conteneur de toasts applicatifs globaux.
 *
 * Affiche les toasts émis via useApp().showToast() en superposition
 * en haut à droite de l'écran. Les toasts s'empilent et disparaissent
 * automatiquement après leur durée (défaut 5s).
 */

import { useApp } from '../contexts/AppContext';
import type { AppToastType, AppToast } from '../types/app';

const TOAST_ICONS: Record<AppToastType, string> = {
  game: '🃏',
  chat: '💬',
  invitation: '🎴',
  table: '📋',
  system: '🔔',
  info: 'ℹ️',
};

function toastIcon(toast: AppToast): string {
  return toast.icon ?? TOAST_ICONS[toast.type] ?? 'ℹ️';
}

function isClickable(toast: AppToast): boolean {
  return !!(toast.libraryNav || toast.targetTab);
}

export default function AppToastContainer() {
  const { toasts, dismissToast, setActiveTab, navigateToLibrary } = useApp();

  if (toasts.length === 0) return null;

  const handleClick = (toast: AppToast) => {
    if (toast.libraryNav) {
      navigateToLibrary(toast.libraryNav);
    } else if (toast.targetTab) {
      setActiveTab(toast.targetTab);
    }
    dismissToast(toast.id);
  };

  return (
    <div className="app-toast-container">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`app-toast app-toast-${toast.type}${isClickable(toast) ? ' app-toast-clickable' : ''}`}
          onClick={() => isClickable(toast) && handleClick(toast)}
          role="alert"
        >
          <span className="app-toast-icon">{toastIcon(toast)}</span>
          <div className="app-toast-body">
            <span className="app-toast-message">{toast.message}</span>
            {toast.subtitle && (
              <span className="app-toast-subtitle">{toast.subtitle}</span>
            )}
          </div>
          {isClickable(toast) && (
            <span className="app-toast-action-label" aria-hidden="true">
              {toast.actionLabel ?? '›'}
            </span>
          )}
          <button
            className="app-toast-close"
            onClick={(e) => { e.stopPropagation(); dismissToast(toast.id); }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
